import { SDK_CAPABILITIES, type SdkCapability } from '@riz/contracts/sdk';
import { SdkClient, SDK_VERSION, type InitOptions, type SdkSession } from './core/client.ts';
import { SDK_PROTOCOL_VERSION, type SdkGame, type SdkPlayer } from './core/protocol.ts';
import { rizError, isRizError, type RizError } from './core/errors.ts';
import { SDK_ENVIRONMENTS, type SdkEnvironment } from './core/environment.ts';
import { createAdsModule, type AdsModule } from './modules/ads.ts';
import { createAnalyticsModule, type AnalyticsModule } from './modules/analytics.ts';
import { createBannerModule, type BannerModule } from './modules/banner.ts';
import { createDataModule, type DataModule } from './modules/data.ts';
import { createDevModule, type DevModule } from './modules/dev.ts';
import { createGameModule, type GameModule } from './modules/game.ts';
import { createLeaderboardModule, type LeaderboardModule } from './modules/leaderboard.ts';
import { createLoadingModule, type LoadingModule } from './modules/loading.ts';
import { createPurchasesModule, type PurchasesModule } from './modules/purchases.ts';
import { createScoreModule, type ScoreModule } from './modules/score.ts';
import { createSocialModule, type SocialModule } from './modules/social.ts';
import { createUserModule, type UserModule } from './modules/user.ts';

export interface RizGamesSdk {
  readonly version: string;
  readonly protocol: number;
  readonly environment: SdkEnvironment;
  readonly Error: typeof rizError;

  init(options: InitOptions): Promise<SdkSession>;
  supports(capability: SdkCapability | string): boolean;
  getSession(): SdkSession | null;
  on<T>(signal: string, handler: (detail: T) => void): () => void;
  off<T>(signal: string, handler: (detail: T) => void): void;
  flush(): Promise<void>;
  destroy(): void;

  game: GameModule;
  score: ScoreModule;
  leaderboard: LeaderboardModule;
  ads: AdsModule;
  banner: BannerModule;
  data: DataModule;
  user: UserModule;
  social: SocialModule;
  analytics: AnalyticsModule;
  purchases: PurchasesModule;
  loading: LoadingModule;
  dev: DevModule;
}

/**
 * Modules are built eagerly but hold no state of their own beyond what the
 * client owns, so constructing them costs nothing and a game can hand
 * `RizGames.ads` to a subsystem before `init()` resolves.
 */
export function createSdk(): RizGamesSdk {
  const client = new SdkClient();

  return {
    version: SDK_VERSION,
    protocol: SDK_PROTOCOL_VERSION,
    Error: rizError,

    get environment() {
      return client.environment;
    },

    init: (options) => client.init(options),
    supports: (capability) => client.capabilities.has(capability),
    getSession: () => client.currentSession,
    on: (signal, handler) => client.emitter.on(signal, handler),
    off: (signal, handler) => client.emitter.off(signal, handler),
    flush: () => client.flush(),
    destroy: () => client.destroy(),

    game: createGameModule(client),
    score: createScoreModule(client),
    leaderboard: createLeaderboardModule(client),
    ads: createAdsModule(client),
    banner: createBannerModule(client),
    data: createDataModule(client),
    user: createUserModule(client),
    social: createSocialModule(client),
    analytics: createAnalyticsModule(client),
    purchases: createPurchasesModule(client),
    loading: createLoadingModule(client),
    dev: createDevModule(client),
  };
}

export const RizGames: RizGamesSdk = createSdk();

export default RizGames;

export {
  SDK_VERSION,
  SDK_ENVIRONMENTS,
  SDK_CAPABILITIES,
  SDK_PROTOCOL_VERSION,
  isRizError,
};

export type {
  InitOptions,
  RizError,
  SdkCapability,
  SdkEnvironment,
  SdkGame,
  SdkPlayer,
  SdkSession,
  AdsModule,
  AnalyticsModule,
  BannerModule,
  DataModule,
  DevModule,
  GameModule,
  LeaderboardModule,
  LoadingModule,
  PurchasesModule,
  ScoreModule,
  SocialModule,
  UserModule,
};
